import { ClosedType, ConditionType, ConditionUtils } from "types/condition";
import { projectionToExpression, aliasedExpression, binaryOperation, ifThen as ifThenExpr, caseCondition, cast, DatePart, datePart, unaryFunction, scalarFunction, aggregateMeasure, aggregateMeasureExpression, column, ifThenWithAlias, isScalar, isColumn, columnProjection, isNonScalarColumn, isColumnProjection, isAliasedProjection, getFunctionInputs, getBinaryOperatorInputs } from "types/expression";
import { filterUndefined, isArrEqual } from "utils/collect";
import moment from "moment";
export function isDateScalar(expr) {
    return typeof expr === "string" && expr.length >= 10 && moment(expr, moment.ISO_8601, true).isValid();
}
export function isOfType(expr, vType) {
    switch (vType) {
        case "BOOLEAN":
            return typeof expr === "boolean";
        case "DATE":
        case "TEXT":
            if (typeof expr !== "string") {
                return false;
            }
            if (isDateScalar(expr)) {
                return vType === "DATE";
            }
            return vType === "TEXT";
        case "NULL":
            return expr === undefined || expr === null;
        case "NUMERIC":
            return typeof expr === "number";
    }
}
export function humanizeType(t) {
    switch (t) {
        case "BOOLEAN":
        case "DATE":
        case "TEXT":
        case "NULL":
            return t[0] + t.toLowerCase().substring(1);
        case "NUMERIC":
            return "Number";
    }
}
export function getDefaultValue(o) {
    switch (o.type) {
        case "BOOLEAN":
            return true;
        case "DATE":
            return moment().startOf("day").format("YYYY-MM-DD");
        case "TEXT":
            return "";
        case "NULL":
            return null;
        case "NUMERIC":
            return 0;
    }
}
export function getValidCastTargets(o) {
    if (o === undefined) {
        return [];
    }
    const targets = [o.type];
    if (o.type == "TEXT") {
        targets.push("NUMERIC");
    }
    else {
        targets.push("TEXT");
    }
    return targets;
}
const TEXT_UNARY_FUNCTIONS = ["trim", "ltrim", "rtrim", "lower", "upper", "parse_date"];
const NUMERIC_UNARY_FUNCTIONS = ["abs", "ln", "log", "log2", "sqrt"];
export function isUnaryFunction(func) {
    return (TEXT_UNARY_FUNCTIONS.find((f) => f === func) !== undefined ||
        NUMERIC_UNARY_FUNCTIONS.find((f) => f === func) !== undefined);
}
export function getUnaryFunctionsForOutput(output) {
    switch (output?.type) {
        case "TEXT":
            return TEXT_UNARY_FUNCTIONS;
        case "NUMERIC":
            return NUMERIC_UNARY_FUNCTIONS;
        default:
            return [];
    }
}
const GENERIC_SCALAR_FUNCTIONS = ["first", "last", "count"];
const NUMERIC_SCALAR_FUNCTIONS = ["sum", "mean", "min", "max"];
export function isScalarFunction(func) {
    return (GENERIC_SCALAR_FUNCTIONS.find((f) => f === func) !== undefined ||
        NUMERIC_SCALAR_FUNCTIONS.find((f) => f === func) !== undefined);
}
export function getScalarFunctionsForOutput(output) {
    const base = GENERIC_SCALAR_FUNCTIONS;
    if (output?.type == "NUMERIC") {
        return base.concat(NUMERIC_SCALAR_FUNCTIONS);
    }
    return base;
}
export function getFunctionsForOutput(output) {
    return getUnaryFunctionsForOutput(output).concat(getScalarFunctionsForOutput(output));
}
const FUNCTION_LABELS = {
    trim: "Trim Whitespace",
    ltrim: "Trim Left Whitespace",
    rtrim: "Trim Right Whitespace",
    lower: "Lowercase",
    upper: "Uppercase",
    parse_date: "Parse Date from String",
    abs: "Absolute Value",
    ln: "Natural Log (ln)",
    log: "Log (log)",
    log2: "Log base 2 (log2)",
    sqrt: "Square Root",
    first: "First Value",
    last: "Last Value",
    sum: "Sum",
    count: "Count",
    max: "Max",
    min: "Min",
    mean: "Mean"
};
export function humanizeFunction(func) {
    return FUNCTION_LABELS[func];
}
const DATE_PART_LABELS = {
    year: "Year",
    quarter: "Quarter",
    month: "Month",
    week: "Week of Year",
    day: "Day",
    hour: "Hour",
    yearweek: "Year with Week",
    dayofyear: "Day of Year",
    epoch: "Epoch",
    //"dow": "DOW",
    isodow: "Day of Week (Monday = 1, Sunday = 7)",
    decade: "Decade",
    century: "Century",
    millennium: "Millennium",
    minute: "Minute",
    second: "Second",
    milliseconds: "Milliseconds",
    microseconds: "Microseconds"
};
export function humanizeDatePart(part) {
    return DATE_PART_LABELS[part];
}
export function getRestrictedContext(context, allowedTypes) {
    return getFilteredContext(context, (o) => allowedTypes.includes(o.type));
}
export function getFilteredContext(context, predicate) {
    return Object.entries(context)
        .filter(([_, o]) => predicate(o))
        .reduce((filtered, [key, t]) => {
        filtered[key] = t;
        return filtered;
    }, {});
}
export function getSchemaColumns(context) {
    return Object.keys(context);
}
export function getNamedOutputs(context) {
    return getSchemaColumns(context).map((n) => ({
        ...context[n],
        name: n
    }));
}
export function getNamedOutput(context, name) {
    if (name === undefined) {
        return undefined;
    }
    const output = context[name];
    if (output === undefined) {
        return undefined;
    }
    return {
        ...output,
        name: name
    };
}
export function isEqual(a, b) {
    if (isScalar(a)) {
        return isScalar(b) && a === b;
    }
    else if (isScalar(b)) {
        return false; // a is non scalar
    }
    else if (isNonScalarColumn(a)) {
        return isNonScalarColumn(b) && a.column === b.column;
    }
    else if (isNonScalarColumn(b)) {
        return false; // a is not column
    }
    switch (a.type) {
        case "AGGREGATE":
            return b.type === "AGGREGATE" && isEqual(a.expr, b.expr) && isEqual(a.filter, b.filter);
        case "BINARY_OPERATION":
            return b.type === "BINARY_OPERATION" && a.operator === b.operator && isArrEqual(a.exprs, b.exprs, isEqual);
        case "CAST":
            return b.type === "CAST" && isEqual(a.expr, b.expr) && a.new_type === b.new_type;
        case "DATE_PART":
            return b.type === "DATE_PART" && isEqual(a.date, b.date) && a.part === b.part;
        case "FUNCTION":
            return b.type === "FUNCTION" && isEqual(a.expr, b.expr) && a.func === b.func;
        case "IF_THEN":
            return (b.type === "IF_THEN" &&
                isEqual(a.else_value, b.else_value) &&
                a.cases.length === b.cases.length &&
                a.cases.every((x, i) => isEqual(x.condition, b.cases[i].condition) && isEqual(x.value, b.cases[i].value)));
    }
    switch (b.type) {
        case "AGGREGATE":
        case "BINARY_OPERATION":
        case "CAST":
        case "DATE_PART":
        case "FUNCTION":
        case "IF_THEN":
            return false;
        default:
            return ConditionUtils.isEqual(a, b);
    }
}
export function isProjectionEqual(a, b) {
    if (typeof a === "string") {
        return typeof b === "string" && a === b;
    }
    else if (typeof b === "string") {
        return false;
    }
    else if (isColumnProjection(a)) {
        return (isColumnProjection(b) &&
            a.column === b.column &&
            (("alias" in a && "alias" in b && a.alias == b.alias) || (!("alias" in a) && !("alias" in b))));
    }
    else if (isColumnProjection(b)) {
        return false;
    }
    else if (a.type === "EXPRESSION") {
        return b.type === "EXPRESSION" && isEqual(a.expr, b.expr) && a.alias === b.alias;
    }
    else if (b.type === "EXPRESSION") {
        return false;
    }
    else if (a.type === "AGGREGATE" && "alias" in a) {
        return b.type === "AGGREGATE" && "alias" in b && isEqual(a, b) && a.alias === b.alias;
    }
    else if (b.type === "AGGREGATE" && "alias" in b) {
        return false;
    }
    else {
        return isEqual(a, b);
    }
}
export function allProjectionsEqual(a, b) {
    return isArrEqual(a, b, isProjectionEqual);
}
export function validateColumn(context, column, allowed_types) {
    const observedType = context[column]?.type;
    if (context[column] === undefined) {
        return [
            {
                column: column,
                available_columns: getSchemaColumns(context)
            }
        ];
    }
    if (allowed_types !== undefined) {
        if (allowed_types.indexOf(observedType) == -1) {
            return [
                {
                    column: column,
                    observed_type: observedType,
                    allowed_types: allowed_types
                }
            ];
        }
    }
    return [];
}
export function validateChildren(context, expr) {
    if (isScalar(expr)) {
        return [];
    }
    else if (!("type" in expr) && "column" in expr) {
        return validateColumn(context, expr.column);
    }
    else if (expr.type == "AGGREGATE") {
        const errors = validate(context, expr.expr);
        if (expr.filter !== undefined) {
            return errors.concat(validate(context, expr.filter, ["BOOLEAN"]));
        }
    }
    else if (expr.type == "BINARY_OPERATION") {
        const outputTypes = getBinaryOperatorInputs(expr.operator);
        return expr.exprs.flatMap((e) => validate(context, e, outputTypes));
    }
    else if (expr.type == "IF_THEN") {
        const conditions = expr.cases.map((c) => c.condition);
        const valueErrors = expr.cases.filter((c) => c.value !== undefined).flatMap((c) => validate(context, c.value));
        return valueErrors.concat(validateAll(context, conditions, ["BOOLEAN"]));
    }
    else if (expr.type == "CAST") {
        return validate(context, expr.expr);
    }
    else if (expr.type == "DATE_PART") {
        return validate(context, expr.date, ["DATE"]);
    }
    else if (expr.type == "FUNCTION") {
        return validate(context, expr.expr, getFunctionInputs(expr.func));
    }
    else if (ConditionUtils.isColumn(expr)) {
        const errors = validateColumn(context, expr.column);
        const outputType = getOutputType(context, column(expr.column));
        if (outputType === undefined) {
            return errors;
        }
        if (ConditionUtils.isComparison(expr)) {
            return errors.concat(validate(context, expr.value, [outputType]));
        }
        else if (ConditionUtils.isInterval(expr)) {
            return errors.concat(validateAll(context, [expr.left, expr.right], [outputType]));
        }
        else if (ConditionUtils.isContainment(expr)) {
            return errors.concat(validateAll(context, expr.values, [outputType]));
        }
    }
    else if (ConditionUtils.isCollection(expr)) {
        return validateAll(context, expr.conditions, ["BOOLEAN"]);
    }
    else if (ConditionUtils.isNot(expr)) {
        return validate(context, expr.condition, ["BOOLEAN"]);
    }
    return [];
}
export function validate(context, expr, allowedTypes) {
    const errors = validateChildren(context, expr);
    if (allowedTypes === undefined) {
        return errors;
    }
    const outputType = getOutputType(context, expr);
    const validAllowedTypes = allowedTypes.filter(filterUndefined);
    if (outputType === undefined || validAllowedTypes.length == 0 || allowedTypes.indexOf(outputType) >= 0) {
        return errors;
    }
    if (outputType == "BOOLEAN" && validAllowedTypes.indexOf("NUMERIC") >= 0) {
        return [];
    }
    return [
        {
            expr: expr,
            output_type: outputType,
            allowed_output_types: allowedTypes.filter(filterUndefined)
        }
    ];
}
export function validateAll(context, expr, allowedTypes) {
    return expr.flatMap((e) => validate(context, e, allowedTypes));
}
export function validateProjection(context, expr, allowedTypes) {
    let errors = [];
    if (isAliasedProjection(expr)) {
        const alias = getAlias(expr);
        errors = alias === undefined || alias === "" ? [{ error_message: "Missing column alias" }] : [];
    }
    return errors.concat(validate(context, projectionToExpression(expr), allowedTypes));
}
export function validateProjections(context, exprs, allowedTypes) {
    return exprs.flatMap((expr) => validateProjection(context, expr, allowedTypes));
}
export function getOutputType(context, expr) {
    return getOutput(context, expr)?.type;
}
function output(type, values = undefined) {
    const output = { type };
    if (values !== undefined) {
        output.values = values;
    }
    return output;
}
export function getOutput(context, expr) {
    if (expr === undefined || expr === null) {
        return output("NULL", [expr]);
    }
    else if (typeof expr === "string") {
        if (isDateScalar(expr)) {
            return output("DATE", [expr]);
        }
        else {
            return output("TEXT", [expr]);
        }
    }
    else if (typeof expr === "boolean") {
        return output("BOOLEAN", [expr]);
    }
    else if (typeof expr === "number") {
        return output("NUMERIC", [expr]);
    }
    if (!("type" in expr) && "column" in expr) {
        return context[expr.column];
    }
    switch (expr.type) {
        case "BINARY_OPERATION":
            return getOutput(context, expr.exprs[0]);
        case "CAST":
            return output(expr.new_type);
        case "IF_THEN":
            return expr.cases
                .map((x) => getOutput(context, x.value))
                .reduce((previousOutput, nextOutput) => {
                const previous = previousOutput?.type;
                const next = nextOutput?.type;
                if (previous == next) {
                    return nextOutput;
                }
                else if (previous == "NULL") {
                    return nextOutput;
                }
                else if (next == "NULL") {
                    return previousOutput;
                }
                else if (previous == undefined ||
                    next == undefined ||
                    previous == "DATE" ||
                    next == "DATE" // noop
                ) {
                    return undefined;
                }
                else if (previous == "TEXT" || next == "TEXT") {
                    return output("TEXT");
                }
                else if (previous == "NUMERIC" || next == "NUMERIC") {
                    return output("NUMERIC");
                }
                else if (previous == "BOOLEAN" || next == "BOOLEAN") {
                    return output("BOOLEAN");
                }
            }, getOutput(context, expr.else_value));
        case "AGGREGATE":
            return getOutput(context, expr.expr);
        case "DATE_PART":
            return output("NUMERIC");
        case "FUNCTION":
            switch (expr.func) {
                case "first":
                case "last":
                    return getOutput(context, expr.expr);
                case "trim":
                case "ltrim":
                case "rtrim":
                case "lower":
                case "upper":
                    return output("TEXT");
                case "parse_date":
                    return output("DATE");
                case "abs":
                case "ln":
                case "log":
                case "log2":
                case "sqrt":
                case "count":
                case "sum":
                case "mean":
                case "min":
                case "max":
                    return output("NUMERIC");
                default:
                    throw Error(`unexpected func`);
            }
        case ConditionType.LessThan:
        case ConditionType.LessThanOrEqual:
        case ConditionType.GreaterThan:
        case ConditionType.GreaterThanOrEqual:
        case ConditionType.Equal:
        case ConditionType.NotEqual:
        case ConditionType.Between:
        case ConditionType.NotBetween:
        case ConditionType.In:
        case ConditionType.NotIn:
        case ConditionType.And:
        case ConditionType.Or:
        case ConditionType.Null:
        case ConditionType.NotNull:
        case ConditionType.Not:
            return output("BOOLEAN");
    }
}
export function getColumnReferences(expr) {
    if (isScalar(expr)) {
        return [];
    }
    if (!("type" in expr) && "column" in expr) {
        return [expr];
    }
    switch (expr.type) {
        case "BINARY_OPERATION":
            return expr.exprs.flatMap(getColumnReferences);
        case "CAST":
        case "AGGREGATE":
        case "FUNCTION":
            return getColumnReferences(expr.expr);
        case "IF_THEN":
            return expr.cases.flatMap((e) => getColumnReferences(e.value)).concat(getColumnReferences(expr.else_value));
        case "DATE_PART":
            return getColumnReferences(expr.date);
        case ConditionType.LessThan:
        case ConditionType.LessThanOrEqual:
        case ConditionType.GreaterThan:
        case ConditionType.GreaterThanOrEqual:
        case ConditionType.Equal:
        case ConditionType.NotEqual:
        case ConditionType.Between:
        case ConditionType.NotBetween:
        case ConditionType.In:
        case ConditionType.NotIn:
        case ConditionType.And:
        case ConditionType.Or:
        case ConditionType.Null:
        case ConditionType.NotNull:
        case ConditionType.Not:
            return ConditionUtils.getColumnReferences(expr);
    }
}
export function getOutputContext(context, exprs) {
    const outputContext = {};
    for (const expr of exprs) {
        const output = getProjectionOutput(context, expr);
        const alias = getAlias(expr);
        if (output !== undefined) {
            outputContext[alias] = output;
        }
    }
    return outputContext;
}
export function getProjectionOutput(context, expr) {
    return getOutput(context, projectionToExpression(expr));
}
export function getAlias(expr) {
    if (typeof expr == "string") {
        return expr;
    }
    else if ("column" in expr) {
        return expr.alias ?? expr.column;
    }
    return expr.alias;
}
export class Builder {
    state;
    constructor(state) {
        this.state = state;
    }
    build() {
        return this.state;
    }
}
export function buildOrReturn(x) {
    return x instanceof Builder ? x.build() : x;
}
export class ExpressionBuilder extends Builder {
    buildAliased(alias) {
        return aliasedExpression(this.state, alias);
    }
    buildAsProjection(alias) {
        return this.buildAliased(alias);
    }
    binaryOp(operator, expr) {
        return new ExpressionBuilder(binaryOperation(operator, [this.state, buildOrReturn(expr)]));
    }
    add(expr) {
        return this.binaryOp("+", expr);
    }
    subtract(expr) {
        return this.binaryOp("-", expr);
    }
    multiply(expr) {
        return this.binaryOp("*", expr);
    }
    divide(expr) {
        return this.binaryOp("/", expr);
    }
    cast(target) {
        return new ExpressionBuilder(cast(this.state, target));
    }
    extractDatePart(part) {
        return new ExpressionBuilder(datePart(this.state, part));
    }
    apply(func) {
        return new ExpressionBuilder(unaryFunction(func, this.state));
    }
    applyScalar(func) {
        return new ExpressionBuilder(scalarFunction(func, this.state));
    }
    aggregate(func, filter) {
        return new AggregateExpressionBuilder(aggregateMeasureExpression(this.applyScalar(func).build(), buildOrReturn(filter)));
    }
}
class ColumnExpressionBuilder extends ExpressionBuilder {
    buildAsProjection(alias) {
        return columnProjection(this.state.column, alias);
    }
    eq(value) {
        return this.compare(ConditionType.Equal, value);
    }
    ne(value) {
        return this.compare(ConditionType.NotEqual, value);
    }
    lt(value) {
        return this.compare(ConditionType.LessThan, value);
    }
    gt(value) {
        return this.compare(ConditionType.GreaterThan, value);
    }
    le(value) {
        return this.compare(ConditionType.LessThanOrEqual, value);
    }
    ge(value) {
        return this.compare(ConditionType.GreaterThanOrEqual, value);
    }
    compare(op, value) {
        return new ConditionBuilder({
            type: op,
            column: this.state.column,
            value: buildOrReturn(value)
        });
    }
    isNotIn(values) {
        return new ConditionBuilder({
            type: ConditionType.NotIn,
            column: this.state.column,
            values: values
        });
    }
    isIn(values) {
        return new ConditionBuilder({
            type: ConditionType.In,
            column: this.state.column,
            values: values
        });
    }
    isBetween(left, right, closed = ClosedType.Both) {
        return new ConditionBuilder({
            type: ConditionType.Between,
            column: this.state.column,
            left: buildOrReturn(left),
            right: buildOrReturn(right),
            closed: closed
        });
    }
    isNotBetween(left, right, closed = ClosedType.Both) {
        return new ConditionBuilder({
            type: ConditionType.NotBetween,
            column: this.state.column,
            left: buildOrReturn(left),
            right: buildOrReturn(right),
            closed: closed
        });
    }
    isNull() {
        return new ConditionBuilder({
            type: ConditionType.Null,
            column: this.state.column
        });
    }
    isNotNull() {
        return new ConditionBuilder({
            type: ConditionType.NotNull,
            column: this.state.column
        });
    }
}
export function builderFrom(expr) {
    if (isColumn(expr)) {
        new ColumnExpressionBuilder(expr);
    }
    return new ExpressionBuilder(expr);
}
export function col(name) {
    return new ColumnExpressionBuilder(column(name));
}
export function scalar(value) {
    return new ExpressionBuilder(value);
}
export function ifThen() {
    return new CaseExpressionBuilder([]);
}
export function andAll(conditions) {
    if (conditions.length == 1) {
        return new ConditionBuilder(buildOrReturn(conditions[0]));
    }
    return new ConditionBuilder({
        type: ConditionType.And,
        conditions: conditions.map((c) => buildOrReturn(c))
    });
}
class CaseExpressionBuilder {
    cases;
    constructor(cases) {
        this.cases = cases;
    }
    else(_else) {
        return new ExpressionBuilder(ifThenExpr(this.cases, buildOrReturn(_else)));
    }
    if(condition, value) {
        this.cases.push(caseCondition(buildOrReturn(condition), buildOrReturn(value)));
        return this;
    }
}
class AggregateExpressionBuilder extends ExpressionBuilder {
    buildAsProjection(alias) {
        return aggregateMeasure(this.state.expr, alias, this.state.filter);
    }
}
class ConditionBuilder extends ExpressionBuilder {
    andWith(condition) {
        return this.combineWith(ConditionType.And, condition);
    }
    orWith(condition) {
        return this.combineWith(ConditionType.Or, condition);
    }
    combineWith(op, condition) {
        condition = buildOrReturn(condition);
        if (this.state.type == op) {
            this.state.conditions.push(condition);
        }
        return new ConditionBuilder({
            type: op,
            conditions: [this.state, condition]
        });
    }
    negate() {
        return new ConditionBuilder({
            type: ConditionType.Not,
            condition: this.state
        });
    }
}
const DEFAULT_CONDITION = andAll([]).build();
class DefaultExpressionFactory {
    binaryOperation() {
        return col("").binaryOp("+", column("")).build();
    }
    column() {
        return column("");
    }
    ifThen() {
        return ifThen().if(col("").eq(""), "").else("").build();
    }
    cast() {
        return cast(column(""), "NULL");
    }
    datePart() {
        return datePart(column(""), DatePart.MONTH);
    }
    scalarFunction() {
        return scalarFunction("first", column(""));
    }
    unaryFunction() {
        return unaryFunction("abs", column(""));
    }
    aggregateMeasure() {
        return aggregateMeasureExpression(this.scalarFunction());
    }
    condition() {
        return DEFAULT_CONDITION;
    }
}
const DEFAULT_EXPRESSION_FACTORY = new DefaultExpressionFactory();
export function defaultExpressionFactory() {
    return DEFAULT_EXPRESSION_FACTORY;
}
class DefaultProjectionFactory {
    factory;
    constructor(factory) {
        this.factory = factory;
    }
    binaryOperation() {
        return aliasedExpression(this.factory.binaryOperation(), "");
    }
    column() {
        return aliasedExpression(this.factory.column(), "");
    }
    ifThen() {
        const ifThen = this.factory.ifThen();
        return ifThenWithAlias(ifThen.cases, ifThen.else_value, "");
    }
    cast() {
        return aliasedExpression(this.factory.cast(), "");
    }
    datePart() {
        return aliasedExpression(this.factory.datePart(), "");
    }
    scalarFunction() {
        return aliasedExpression(this.factory.scalarFunction(), "");
    }
    unaryFunction() {
        return aliasedExpression(this.factory.unaryFunction(), "");
    }
    aggregateMeasure() {
        return aggregateMeasure(this.factory.scalarFunction(), "");
    }
}
const DEFAULT_PROJECTION_FACTORY = new DefaultProjectionFactory(DEFAULT_EXPRESSION_FACTORY);
export function defaultProjectionFactory() {
    return DEFAULT_PROJECTION_FACTORY;
}
